import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection 
} from '@/components/WorkbookPage';
import { CreditCard, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';

export const Rule9Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule9 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule9', { ...data, [field]: value });
  };
  
  return (
    <WorkbookPage pageId="rule-9" prevPage="section-3-divider" nextPage="rule-10">
      <RuleHeader ruleNumber="9" title="Understand Credit" section="growth" />
      
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        Credit is a tool that lets you borrow money now and pay it back later. Used wisely, 
        it helps you build a good reputation (credit score) that unlocks better rates on loans, 
        apartments, and even jobs. Used poorly, it becomes a trap of debt and fees.
      </Paragraph>
      
      <SectionTitle>How Credit Works</SectionTitle>
      
      <div className="space-y-6 my-8">
        <div className="bg-secondary/50 rounded-lg p-5">
          <h4 className="font-semibold flex items-center gap-2 mb-3">
            <CreditCard className="h-5 w-5 text-primary" />
            Credit Cards
          </h4>
          <p className="text-sm text-muted-foreground mb-3">
            You're given a credit limit (say $1,000). You can spend up to that amount, then 
            you must pay it back. If you pay the full balance each month, you pay no interest. 
            If you only pay the minimum, you pay A LOT in interest.
          </p>
          <div className="grid gap-2 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-success" />
              <span><strong>Good use:</strong> Buy $200, pay $200 when bill comes</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <span><strong>Bad use:</strong> Max it out, pay only minimum ($25)</span>
            </div>
          </div>
        </div>
        
        <div className="bg-secondary/50 rounded-lg p-5">
          <h4 className="font-semibold mb-3">Credit Utilization</h4>
          <p className="text-sm text-muted-foreground mb-3">
            This is how much of your available credit you're using. Keep it under 30% for a 
            good score—under 10% is even better.
          </p>
          <div className="bg-card rounded p-3">
            <p className="text-sm">
              Example: Credit limit is $1,000 → Keep balance under $300 (30%)
            </p>
          </div>
        </div>
        
        <div className="bg-secondary/50 rounded-lg p-5">
          <h4 className="font-semibold flex items-center gap-2 mb-3">
            <TrendingUp className="h-5 w-5 text-primary" />
            Your Credit Score
          </h4>
          <p className="text-sm text-muted-foreground mb-3">
            A number from 300-850 that represents your credit reputation. Higher is better.
          </p>
          <div className="grid grid-cols-5 gap-1 text-center text-xs">
            <div className="bg-destructive/20 rounded py-2">
              <div className="font-semibold">300-579</div>
              <div className="text-muted-foreground">Poor</div>
            </div>
            <div className="bg-warning/20 rounded py-2">
              <div className="font-semibold">580-669</div>
              <div className="text-muted-foreground">Fair</div>
            </div>
            <div className="bg-accent/20 rounded py-2">
              <div className="font-semibold">670-739</div>
              <div className="text-muted-foreground">Good</div>
            </div>
            <div className="bg-success/20 rounded py-2">
              <div className="font-semibold">740-799</div>
              <div className="text-muted-foreground">Very Good</div>
            </div>
            <div className="bg-primary/20 rounded py-2">
              <div className="font-semibold">800-850</div>
              <div className="text-muted-foreground">Excellent</div>
            </div>
          </div>
        </div>
      </div>
      
      <SectionTitle>When to Use Credit (and When NOT To)</SectionTitle>
      
      <div className="grid md:grid-cols-2 gap-4 my-6">
        <div className="border border-success rounded-lg p-4">
          <h4 className="font-semibold text-success mb-3">✓ Good Uses</h4>
          <ul className="text-sm space-y-2">
            <li>• Building credit history (small purchases, paid in full)</li>
            <li>• Emergency when you have a payback plan</li>
            <li>• Earning rewards (only if paid in full monthly)</li>
            <li>• Large purchases you've saved for (to build credit)</li>
          </ul>
        </div>
        <div className="border border-destructive rounded-lg p-4">
          <h4 className="font-semibold text-destructive mb-3">✗ Bad Uses</h4>
          <ul className="text-sm space-y-2">
            <li>• Buying things you can't afford</li>
            <li>• Living beyond your means</li>
            <li>• Paying for wants with no payback plan</li>
            <li>• Cash advances (extremely high fees)</li>
          </ul>
        </div>
      </div>
      
      <Callout type="warning" title="The Minimum Payment Trap">
        Credit card companies want you to pay the minimum. On a $1,000 balance at 24% APR, 
        paying only $25/month takes over 5 years and costs $600+ in interest. Always pay 
        more than the minimum when possible.
      </Callout>
      
      <ExampleBox title="Good Credit Card Use">
        <p><strong>Aisha</strong> uses her credit card for groceries ($200/month). 
        She has the cash in her checking account but uses the card to build credit. 
        When the bill comes, she pays the full $200. She earns 2% cash back ($4) 
        and pays $0 in interest. Her credit score improves over time.</p>
      </ExampleBox>
      
      <StepList steps={[
        "Check your credit score for free (Credit Karma, your bank, etc.).",
        "Review your credit report annually at AnnualCreditReport.com.",
        "If you have credit cards, always try to pay the full balance.",
        "Keep utilization under 30% of your limit.",
        "Never miss a payment—set up autopay for at least the minimum.",
        "Don't open lots of new accounts at once (hurts your score)."
      ]} />
      
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">📝 Your Credit Worksheet</h3>
        
        <div className="space-y-6">
          <WorksheetInput
            label="My current credit score (check for free at Credit Karma, etc.):"
            placeholder="___"
            value={data.creditScore || ''}
            onChange={(v) => updateData('creditScore', v)}
          />
          
          <div>
            <label className="block text-sm font-medium mb-3">My credit cards:</label>
            <div className="space-y-3">
              {[1, 2, 3].map((num) => (
                <div key={num} className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    className="border-b border-dashed border-border bg-transparent py-2 focus:outline-none"
                    placeholder={`Card ${num} name`}
                    value={data[`card${num}Name`] || ''}
                    onChange={(e) => updateData(`card${num}Name`, e.target.value)}
                  />
                  <input
                    type="text"
                    className="border-b border-dashed border-border bg-transparent py-2 text-right focus:outline-none"
                    placeholder="Balance"
                    value={data[`card${num}Balance`] || ''}
                    onChange={(e) => updateData(`card${num}Balance`, e.target.value)}
                  />
                  <input
                    type="text"
                    className="border-b border-dashed border-border bg-transparent py-2 text-right focus:outline-none"
                    placeholder="Limit"
                    value={data[`card${num}Limit`] || ''}
                    onChange={(e) => updateData(`card${num}Limit`, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
          
          <WorksheetInput
            label="My credit goals (improve score, pay off cards, etc.):"
            placeholder="What do you want to achieve?"
            value={data.creditGoals || ''}
            onChange={(v) => updateData('creditGoals', v)}
            multiline
          />
        </div>
      </div>
      
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem checked={data.checklist1 || false} onChange={(v) => updateData('checklist1', v)}>
          I understand how credit cards work
        </CheckboxItem>
        <CheckboxItem checked={data.checklist2 || false} onChange={(v) => updateData('checklist2', v)}>
          I know my current credit score (or will check it)
        </CheckboxItem>
        <CheckboxItem checked={data.checklist3 || false} onChange={(v) => updateData('checklist3', v)}>
          I understand credit utilization
        </CheckboxItem>
        <CheckboxItem checked={data.checklist4 || false} onChange={(v) => updateData('checklist4', v)}>
          I have a plan for responsible credit use
        </CheckboxItem>
      </div>
      
      <RecapBox items={[
        "Credit is a tool—use it wisely",
        "Pay full balance monthly to avoid interest",
        "Keep utilization under 30%",
        "Never miss a payment",
        "Check your score regularly (free)"
      ]} />
      
      <AffirmationBox>
        I use credit as a tool to build my future, not a trap that holds me back.
      </AffirmationBox>
      
      <OptionalSection>
        <WorksheetInput
          label="Your thoughts:"
          placeholder="What's your relationship with credit been like?"
          value={data.reflection || ''}
          onChange={(v) => updateData('reflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
